import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { config } from '../config.js';
import { boot } from '../lib/boot.js';

/**
 * Atualização OTA do bundle web (só no app Android; o iOS não tem o plugin — regra 2.5.2 da Apple).
 *
 * Ao ABRIR o app (bootUpdate, antes das rotas):
 *  1. notifyAppReady(): avisa o plugin que ESTE bundle subiu bem. Se um bundle novo
 *     não chamar isso em até appReadyTimeout (10 s), o plugin volta ao anterior.
 *  2. Lê o manifest do canal (S3) com limite de 3 s. Sem resposta, segue com a versão atual.
 *  3. Versão maior e compatível: tela cheia "Atualizando o app" com a porcentagem;
 *     baixa, confere o SHA-256, descompacta e aplica na hora (set → o app reabre já na nova).
 *     Qualquer falha: segue com a versão atual e tenta de novo na próxima abertura.
 *
 * Ao VOLTAR do segundo plano (resume): confere em silêncio e, se houver versão nova,
 * baixa e agenda (next) para a próxima abertura. Não interrompe quem está usando.
 *
 * Manifest: { env, version, url, sha256, size, minNativeVersion, releasedAt }
 *
 * Ambientes: o app "TicketCar DEV" (com.ticketcar.app.dev) só aceita bundles/manifests
 * de DEV; o "TicketCar" (com.ticketcar.app) só de PRD. Três travas:
 *  - a URL do manifest vem embutida no bundle do ambiente;
 *  - o manifest precisa ter o mesmo "env" do bundle;
 *  - o bundle precisa ser do mesmo ambiente da variante nativa (id do app).
 */

const FAILED_KEY = 'tc.ota.failed';
const CHECK_INTERVAL_MS = 60_000;
const MANIFEST_TIMEOUT_MS = 3_000;
const DOWNLOAD_TIMEOUT_MS = 90_000;

let checking = false;
let lastCheck = 0;
let envMismatch = false;

const log = (...args) => console.info('[ota]', ...args);

/**
 * Compara "X.Y.Z" e "X.Y.Z-rc.N" (1.2.0-rc.3 < 1.2.0-rc.4 < 1.2.0 < 1.2.1-rc.1).
 * DEV recebe candidatas (rc) geradas pelo CI a cada push em release/*; PRD só versões finais.
 * Sufixos desconhecidos (ex.: versionName nativo "1.2.0-dev") contam como versão final.
 */
export function compareVersions(a, b) {
  const parse = (v) => {
    const m = /^(\d+)\.(\d+)\.(\d+)(?:-rc\.(\d+))?/.exec(String(v).trim());
    return m ? [+m[1], +m[2], +m[3], m[4] === undefined ? Infinity : +m[4]] : [0, 0, 0, 0];
  };
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < 4; i++) if (pa[i] !== pb[i]) return pa[i] > pb[i] ? 1 : -1;
  return 0;
}

const failedVersions = () => {
  try {
    return JSON.parse(localStorage.getItem(FAILED_KEY)) ?? [];
  } catch {
    return [];
  }
};

function markFailed(version) {
  if (!version) return;
  const list = new Set(failedVersions());
  list.add(version);
  try {
    localStorage.setItem(FAILED_KEY, JSON.stringify([...list]));
  } catch {
    /* ignore */
  }
}

/** Ambiente da variante nativa, pelo id do app: com.ticketcar.app.dev → dev. */
const nativeEnvOf = (appId) => (String(appId).endsWith('.dev') ? 'dev' : 'prd');

const withTimeout = (promise, ms, what) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error(`${what}: tempo esgotado`)), ms))]);

/**
 * Busca o manifest e decide se há versão nova aplicável.
 * @returns {Promise<{ manifest: any, existing: any } | null>}
 */
async function findUpdate(timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let manifest;
  try {
    const res = await fetch(`${config.ota.manifestUrl}?t=${Date.now()}`, { cache: 'no-store', signal: ctrl.signal });
    if (!res.ok) return log('manifest indisponível', res.status), null;
    manifest = await res.json();
  } finally {
    clearTimeout(timer);
  }

  if (manifest.env !== config.appEnv) return log(`manifest de outro ambiente (${manifest.env}), ignorando. Este app é ${config.appEnv}.`), null;
  const current = config.bundleVersion;
  if (compareVersions(manifest.version, current) <= 0) return log('já na versão mais nova', current), null;
  if (failedVersions().includes(manifest.version)) return log('versão marcada como falha, ignorando', manifest.version), null;

  const { version: nativeVersion } = await App.getInfo();
  if (manifest.minNativeVersion && compareVersions(nativeVersion, manifest.minNativeVersion) < 0) {
    return log(`versão ${manifest.version} exige app nativo >= ${manifest.minNativeVersion} (atual ${nativeVersion})`), null;
  }

  const { bundles } = await CapacitorUpdater.list();
  const existing = bundles.find((b) => b.version === manifest.version) ?? null;
  if (existing?.status === 'error') {
    markFailed(manifest.version);
    return log('versão já falhou antes, ignorando', manifest.version), null;
  }
  return { manifest, existing };
}

/**
 * Roda ao abrir o app, ANTES das rotas. Resolve quando o app deve seguir
 * (sem atualização, ou falhou). Quando aplica uma versão nova, o app reabre e esta
 * promessa nunca resolve.
 */
export async function bootUpdate() {
  boot.version(`versão ${config.bundleVersion}${config.appEnv === 'prd' ? '' : ` · ${config.appEnv.toUpperCase()}`}`);
  if (!Capacitor.isNativePlatform()) return;
  // No iPhone o app não tem o plugin de OTA (regra 2.5.2 da App Store: nada de trocar o código depois da
  // revisão). Lá o frontend novo só chega com um build novo do app. O OTA fica só no Android.
  if (!Capacitor.isPluginAvailable('CapacitorUpdater')) return;

  const info = await App.getInfo().catch(() => null);
  if (info && nativeEnvOf(info.id) !== config.appEnv) {
    // Ex.: APK de produção gerado com bundle de DEV embutido. Não atualiza nada.
    envMismatch = true;
    console.error(`[ota] bundle ${config.appEnv} dentro do app ${info.id}: ambiente trocado, OTA desativado.`);
  }

  // Bundle que falhou ao subir: o plugin já voltou para o anterior; não baixamos de novo.
  CapacitorUpdater.addListener('updateFailed', (e) => {
    log('bundle falhou e foi revertido:', e?.bundle?.version);
    markFailed(e?.bundle?.version);
  });

  try {
    await CapacitorUpdater.notifyAppReady();
    log('bundle ativo:', config.bundleVersion);
  } catch (err) {
    log('notifyAppReady falhou', err);
  }

  App.addListener('resume', () => checkInBackground());
  if (envMismatch || !config.ota.manifestUrl) return;

  lastCheck = Date.now();
  checking = true;
  let listener;
  try {
    boot.checking();
    const found = await findUpdate(MANIFEST_TIMEOUT_MS);
    if (!found) return;
    const { manifest, existing } = found;
    const from = config.bundleVersion;

    let bundle = existing;
    if (!bundle) {
      boot.progress(0, { from, to: manifest.version, env: config.appEnv, totalBytes: manifest.size });
      listener = await CapacitorUpdater.addListener('download', (e) => boot.progress(e?.percent ?? 0, { totalBytes: manifest.size }));
      bundle = await withTimeout(
        CapacitorUpdater.download({ url: manifest.url, version: manifest.version, checksum: manifest.sha256 }),
        DOWNLOAD_TIMEOUT_MS,
        'download',
      );
    }
    boot.installing();
    log(`aplicando ${manifest.version} agora`);
    await CapacitorUpdater.set({ id: bundle.id }); // reabre o app já na versão nova
    await new Promise(() => {}); // não segue: o WebView vai recarregar
  } catch (err) {
    log('atualização na abertura não concluída; seguindo com a versão atual:', err?.message ?? err);
  } finally {
    listener?.remove?.();
    checking = false;
  }
}

/** Ao voltar do segundo plano: baixa em silêncio e aplica na próxima abertura. */
async function checkInBackground() {
  if (!Capacitor.isPluginAvailable('CapacitorUpdater')) return;
  if (checking || envMismatch || !config.ota.manifestUrl) return;
  if (Date.now() - lastCheck < CHECK_INTERVAL_MS) return;
  checking = true;
  lastCheck = Date.now();
  try {
    const found = await findUpdate(10_000);
    if (!found) return;
    const { manifest, existing } = found;
    const bundle = existing ?? (await CapacitorUpdater.download({ url: manifest.url, version: manifest.version, checksum: manifest.sha256 }));
    await CapacitorUpdater.next({ id: bundle.id });
    log(`versão ${manifest.version} baixada; aplica na próxima abertura`);
  } catch (err) {
    log('falha ao verificar/baixar atualização', err?.message ?? err);
  } finally {
    checking = false;
  }
}
